import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { fetchAdminQueueSummary, fetchAdminTripsSummary } from '../services/api/admin';
import type { AdminQueueSummary, AdminTripsSummary } from '../types/admin';
import { formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const AdminDashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [queueSummary, setQueueSummary] = useState<AdminQueueSummary | null>(null);
  const [tripsSummary, setTripsSummary] = useState<AdminTripsSummary | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async (mode: 'initial' | 'refresh' = 'initial') => {
      if (mode === 'initial') {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError('');
      try {
        const [queue, trips] = await Promise.all([
          fetchAdminQueueSummary(),
          fetchAdminTripsSummary(),
        ]);
        if (!mounted) return;
        setQueueSummary(queue);
        setTripsSummary(trips);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load the admin overview.');
      } finally {
        if (!mounted) return;
        setLoading(false);
        setRefreshing(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      const [queue, trips] = await Promise.all([
        fetchAdminQueueSummary(),
        fetchAdminTripsSummary(),
      ]);
      setQueueSummary(queue);
      setTripsSummary(trips);
    } catch (refreshError: any) {
      setError(refreshError.message || 'Could not refresh the admin overview.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <AdminShell
      badge="Admin Overview"
      copy="Track driver onboarding queues and operational trip activity from one place."
      title="Platform admin dashboard"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}

      {loading ? (
        <div className="admin-panel">
          <div className="register-loader" />
        </div>
      ) : (
        <>
          <div className="admin-grid admin-grid-cards">
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Submitted drivers</span>
              <strong>{queueSummary?.submittedDriversCount ?? 0}</strong>
              <span className="admin-stat-note">Drivers waiting for onboarding review.</span>
            </div>
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Accepted trips today</span>
              <strong>{tripsSummary?.acceptedTripsToday ?? 0}</strong>
              <span className="admin-stat-note">Trips accepted today in the Africa/Johannesburg reporting window.</span>
            </div>
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">In progress trips today</span>
              <strong>{tripsSummary?.inProgressTripsToday ?? 0}</strong>
              <span className="admin-stat-note">Today&apos;s trips in LOADING, PICKED_UP, ENROUTE, UNLOADING, or DELIVERED.</span>
            </div>
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Rejected trips today</span>
              <strong>{tripsSummary?.rejectedTripsToday ?? 0}</strong>
              <span className="admin-stat-note">Trips rejected today using the backend&apos;s effective rejection timestamp.</span>
            </div>
          </div>

          <div className="admin-grid admin-grid-main">
            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Latest submitted drivers</h2>
                  <p className="admin-panel-copy">These are the newest profiles currently waiting in the review queue.</p>
                </div>
                <div className="admin-inline-actions">
                  <button
                    className="register-secondary-button"
                    disabled={refreshing}
                    onClick={handleRefresh}
                    type="button"
                  >
                    {refreshing ? 'Refreshing...' : 'Refresh dashboard'}
                  </button>
                </div>
              </div>
              <div className="admin-list">
                {(queueSummary?.latestSubmittedDrivers ?? []).map((driver) => (
                  <div className="admin-list-card" key={driver.driverId}>
                    <strong>{driver.name}</strong>
                    <small>{driver.email}</small>
                    <small>
                      {driver.accountType || 'Account type pending'} • submitted{' '}
                      {driver.onboardingSubmittedAt ? formatDateTime(driver.onboardingSubmittedAt) : 'Not available'}
                    </small>
                    <div className="admin-pill-row">
                      <span className="admin-pill">{driver.onboardingReviewStatus || 'PENDING'}</span>
                      <span className="admin-pill">{driver.onboardingsStep || 'SUBMITTED'}</span>
                    </div>
                    <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
                      <Link className="register-submit register-submit-inline" to={`/admin/drivers/${driver.driverId}`}>
                        Review profile
                      </Link>
                    </div>
                  </div>
                ))}
                {!queueSummary?.latestSubmittedDrivers?.length ? (
                  <p className="admin-empty">No submitted drivers are waiting right now.</p>
                ) : null}
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Trip operations snapshot</h2>
                  <p className="admin-panel-copy">A quick view of current trip workload based on the backend trip summary.</p>
                </div>
                <Link className="register-secondary-button" to="/admin/trips">
                  Open trips
                </Link>
              </div>
              <div className="admin-detail-grid">
                <div className="admin-detail-card">
                  <span>Total trips</span>
                  <strong>{tripsSummary?.totalTrips ?? 0}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Active trips</span>
                  <strong>{tripsSummary?.activeTrips ?? 0}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Completed trips</span>
                  <strong>{tripsSummary?.completedTrips ?? 0}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Cancelled trips</span>
                  <strong>{tripsSummary?.cancelledTrips ?? 0}</strong>
                </div>
              </div>
              <p className="admin-muted-note" style={{ marginTop: '16px' }}>
                Today counters are now provided by the backend summary and follow the Africa/Johannesburg
                business day definition used for admin reporting.
              </p>
            </section>
          </div>
        </>
      )}
    </AdminShell>
  );
};

export default AdminDashboardPage;
